import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { NavigationProps, MenuListProps, NavigationOptionProps } from './Navigation.types';
import PropTypes from 'prop-types';
import Icon from '../icon/Icon';
import './navigation.scss';

const Navigation = ({ userName, userRole, userImage, options, optionsBottom }: NavigationProps) => {
  const [stateOpen, setStateOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>();
  const el = document.createElement('div');
  const open = stateOpen ? 'sb-navigation__open--open' : '';

  useEffect(() => {
    const updateWidth = () => {
      const isMobile = document.body.clientWidth < 768

      setIsMobile(isMobile)
    }

    updateWidth()
    window.addEventListener("resize", updateWidth)
  })

  useEffect(() => {
    if (!document.querySelector('.sb-navigation__open--overlay')) {
      const body = document.querySelector('body');

      body?.appendChild(el);
    }
  }, [el]);

  const closeModal = () => setStateOpen(false);

  return (
    <div
      className="sb-navigation"
      data-testid="sb-navigation"
    >
      <div
        className="sb-navigation__button"
        data-testid="sb-navigation__button"
        onClick={() => { setStateOpen(!stateOpen) }}
      >
        <Icon size={isMobile ? 16 : 30}
          iconName="menu"
        />
      </div>
      {!isMobile &&
        <>
          <MenuList
            className="block sb-navigation__items--top"
            isMobile={isMobile}
          >
            {options?.map((option, index) => (<MenuItem
              url={option.url}
              text={option.text}
              iconName={option.iconName}
              inLine={false}
              className="sb-navigation__item"
              key={`sb-navigation__item-${index}`}
              isMobile={isMobile}
            />))}
          </MenuList>
          <MenuList
            className="block sb-navigation__items--bottom"
            isMobile={isMobile}
          >
            {optionsBottom.map((option, index) => (<MenuItem
              url={option.url}
              text={option.text}
              iconName={option.iconName}
              inLine={false}
              className="sb-navigation__item"
              key={`sb-navigation__item-${index}`}
              isMobile={isMobile}
            />))}
          </MenuList>
        </>
      }
      {stateOpen && createPortal(
        <div
          className={['sb-navigation__open--overlay',
            open].join(' ')}
          data-testid="sb-navigation__open--overlay"
        >
          <div
            className="sb-navigation__open--container"
            data-testid="sb-navigation__open--container"
          >
            <div
              className="sb-navigation__open"
              data-testid="sb-navigation__open"
            >
              <div
                className="sb-navigation__button"
                data-testid="sb-navigation__button"
                onClick={closeModal}
              >
                <Icon size={isMobile ? 16 : 30}
                  iconName="arrow-left"
                />
              </div>
              {isMobile &&
                <div className="sb-navigation__profile">
                  <img
                    src={userImage}
                    alt=""
                    data-testid="sb-navigation__profile__image"
                  />
                  <div className="sb-navigation__profile--information">
                    Hola, <strong>{userName}</strong>
                    <p>{userRole}</p>
                  </div>
                </div>
              }
              <MenuList
                className="block sb-navigation__items--top"
                isMobile={isMobile}
              >
                {options?.map((option, index) => (<MenuItem
                  url={option.url}
                  text={option.text}
                  iconName={option.iconName}
                  inLine={true}
                  className="sb-navigation__item"
                  isMobile={isMobile}
                  key={`sb-navigation__item-${index}`}
                />))}
              </MenuList>
              <MenuList
                className="block sb-navigation__items--bottom"
                isMobile={isMobile}
              >
                {optionsBottom.map((option, index) => (<MenuItem
                  url={option.url}
                  text={option.text}
                  iconName={option.iconName}
                  inLine={true}
                  className="sb-navigation__item"
                  isMobile={isMobile}
                  key={`sb-navigation__item-${index}`}
                />))}
              </MenuList>
            </div>
          </div>
        </div>, el
      )}
    </div>
  );
};

const pxToRem = (px: number) => {
  return px * 0.1;
};

const height = {
  mobile: 56,
  desktop: 80,
};

const spacing = {
  mobile: 0,
  desktop: 0,
};

const MenuList = ({ children, className, isMobile }: MenuListProps) => {
  const [linePosition, setLinePosition] = useState(0);
  const [itemStyles, setItemStyles] = useState({
    height: height.desktop,
    spacing: spacing.desktop,
  });

  useEffect(() => {
    setItemStyles(
      isMobile
        ? { height: height.mobile, spacing: spacing.mobile }
        : { height: height.desktop, spacing: spacing.desktop }
    );
  }, [isMobile]);

  useEffect(() => {
    const moveLine = () => {
      const currentIndex = children?.findIndex((item: any) => {
        return item?.props?.exact
          ? item.props.url === window.location.pathname
          : window.location.pathname.startsWith(item.props.url);
      });

      const newLinePosition = pxToRem(
        currentIndex ?
          (currentIndex * itemStyles.height + currentIndex * itemStyles.spacing) : 0
      );

      setLinePosition(newLinePosition);
    };

    moveLine();
  }, [children, itemStyles]);

  return (
    <div className={`relative h-full lg:pl-none lg:h-auto ${className}`}>
      {children}
      {linePosition >= 0 && (
        <div
          className="sb-navigation__line"
          style={{
            transform: `translateY(${linePosition}rem)`,
            height: `${pxToRem(itemStyles.height)}rem`,
          }}
        />
      )}
    </div>
  );
};

const MenuItem = ({
  text,
  iconName,
  url,
  className,
  inLine,
  isMobile
}: NavigationOptionProps) => {
  const [active, setActive] = useState(false);
  const location = window.location.pathname;

  useEffect(() => {
    setActive(url === location ? true : false)
  }, [location, url]);

  return (
    <div className={`${className} ${active && "sb-navigation__item--active"}`}>
      <a href={url}>
        <div
          className={`flex ${inLine
            ? "flex-row items-center"
            : "flex-col items-center"
            }`}
        >
          <Icon size={isMobile ? 16 : 24} iconName={iconName} />
          {(!inLine && !isMobile) &&
            <div
              className={`mt-xsm mb-none ${inLine
                ? "ml-xlg text-body font-regular"
                : "text-small-02 font-regular leading-6 truncate"
                }`}
              style={{ maxWidth: inLine ? 'initial' : '50px' }}
            >
              {text}
            </div>
          }
          {inLine &&
            <div
              className={`${text ? "" : "mt-xsm"} ${inLine
                ? "ml-xlg text-body font-regular"
                : "text-small-02 font-regular leading-6 truncate"}`}
              style={{ maxWidth: inLine ? 'initial' : '56px' }}
            >
              {text}
            </div>
          }
        </div>
      </a>
    </div>
  );
};

Navigation.propTypes = {
  /**
* userName
*/
  userName: PropTypes.string,
  /**
* userRole
*/
  userRole: PropTypes.string,
  /**
* userImage
*/
  userImage: PropTypes.string,
  /**
* options
*/
  options: PropTypes.array,
  /**
* optionsBottom
*/
  optionsBottom: PropTypes.array,
};

Navigation.defaultProps = {
  userName: "Alexander",
  userRole: "Estudiante",
  userImage: "https://i.ibb.co/QKkpfgc/user-image-2.png",
  options: [
    {
      text: "Curso",
      iconName: "book",
      url: "/iframe.html",
      className: "",
    },
    {
      text: "Chat",
      iconName: "message-circle",
      url: "/xx",
      className: "",
    }
  ],
  optionsBottom: [
    {
      text: "Ayuda",
      iconName: "settings",
      url: "/xx",
      className: "",
    }
  ]
};

export default Navigation;